import React, { useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { FabricPlayer } from "../imports";
import { getFile } from "../redux/creators/fileCreators";
import { size, device } from "../../../utils/breakPoints";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

/**
 * Image thumbnails and gallery for PDP builder
 *
 * @returns {Object}
 */
export default function BuilderImageGallery() {
  // Get images from shopify product
  const [imageIndex, setImageIndex] = useState(0);

  /**
   * Store current user file for thumbnail
   * For multiple images, just use the first
   * For no uploaded images, use logo block
   */
  const { file, files } = useSelector((state) => getFile(state));
  const firstFile = file || files[Object.keys(files)[0]] || null;
  const fileUrl = firstFile !== null
    ? URL.createObjectURL(firstFile)
    : "https://cdn.shopify.com/s/files/1/0591/4299/6149/files/logo_block.png";

  // Create product images array for gallery (max 4 images)
  const productImages = [fileUrl, ...window.product.images].slice(0,4);
  const hasProductImages = productImages.length > 1;

  // Return image based on selection
  const getCurrentImage = () => {
    // Get react styled component for builder container
    const playerClassName = FabricPlayerContainer.styledComponentId;
    const builder = document.getElementsByClassName(playerClassName)[0] || null;

    // Make sure builder is found
    if (builder === null) return;

    // Return image and show/hide the builder
    if (imageIndex !== 0) {
      builder.style.display = "none";
      return (
        <Image src={productImages[imageIndex]}></Image>
      );
    } else {
      builder.style.display = "block";
    }
  };

  // Return thumbnail images
  const getThumbnails = () => {
    return productImages.map((image, index) => {
      return (
        <Image
          src={image}
          key={index}
          onClick={() => setImageIndex(index)}
        ></Image>
      );
    });
  };

  // Mobile arrow image update
  const changeImage = (direction) => {
    const newIndex = imageIndex + direction;
    const numOfImages = productImages.length - 1;

    // Make sure selected image is within bounds
    if (newIndex <= numOfImages && newIndex >= 0) {
      setImageIndex(newIndex);
    }
  }

  // If mobile arrows should show
  const shouldShowArrow = (direction) => {
    return direction === "left"
      ? imageIndex > 0
      : imageIndex < productImages.length - 1;
  }

  return (
    <Container>
      <ImageThumbnailContainer>
        {getThumbnails()}
      </ImageThumbnailContainer>
      <BuilderContainer>
        {hasProductImages && (
          <Arrow
            icon={faChevronLeft}
            onClick={() => changeImage(-1)}
            className={shouldShowArrow("left") ? "" : "disabled-arrow"}
          ></Arrow>
        )}
        <ImageContainer>
          {getCurrentImage()}
        </ImageContainer>
        <FabricPlayerContainer>
          <FabricPlayer />
        </FabricPlayerContainer>
        {hasProductImages && (
          <Arrow
            icon={faChevronRight}
            onClick={() => changeImage(1)}
            className={shouldShowArrow("right") ? "" : "disabled-arrow"}
          ></Arrow>
        )}
      </BuilderContainer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`;
const ImageThumbnailContainer = styled.div`
  width: 100px;
  min-width: 100px;
  max-width: 100px;
  @media ${device.tablet} {
    display: none;
  }
`;
const BuilderContainer = styled.div`
  width: 100%;
  display: grid;
  @media ${device.tablet} {
    display: inline-grid;
    grid-template-columns: 8% 84% 8%;
  }
  .disabled-arrow {
    visibility: hidden;
  }
`;
const ImageContainer = styled.div`
  grid-row: 1;
  grid-column: 1;
  z-index: 2;
  @media ${device.tablet} {
    grid-column: 2;
  }
`;
const FabricPlayerContainer = styled.div`
  grid-row: 1;
  grid-column: 1;
  z-index: 1;
  @media ${device.tablet} {
    grid-column: 2;
  }
`;
const Image = styled.img`
  width: 100%;
`;
const Arrow = styled(FontAwesomeIcon)`
  width: 12px;
  padding: 50px 5px;
  margin-top: 50px;
  margin-right: auto;
  margin-left: auto;
  @media (min-width: ${size.tablet}) {
    display: none;
  }
`;
